/**
 * WordPress dependencies
 */
import { Button, Modal } from '@safe-wordpress/components';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { last } from 'lodash';
import { store as NAB_DATA } from '@nab/data';
import type { ExperimentSuggestion, Maybe, Opportunity } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { CreateExperimentFromOpportunityButton } from '../create-experiment-from-opportunity-button';
import { LoadingAnimation } from '@nab/components';
import { useEffect, useState } from '@wordpress/element';

export function ExperimentSuggestionsModal(): JSX.Element | null {
	const { opportunityBeingInstantiated, state } = useSelect(
		( select ) => ( {
			opportunityBeingInstantiated:
				select( NAB_DATA ).getOpportunityBeingInstantiated(),
			state: select( NAB_DATA ).getSuggestionGenerationState(),
		} ),
		[]
	);
	const { setSuggestionGenerationState } = useDispatch( NAB_DATA );

	if ( 'idle' === state.status ) {
		return null;
	}

	const isLoading = 'opportunity-scan-ready' !== state.status;
	const isDismissible = ! isLoading && ! opportunityBeingInstantiated;

	return (
		<Modal
			className="nab-overview-suggestions-modal"
			title={ _x( 'Test Suggestions', 'text', 'nelio-ab-testing' ) }
			isDismissible={ isDismissible }
			shouldCloseOnEsc={ isDismissible }
			shouldCloseOnClickOutside={ isDismissible }
			onRequestClose={ () =>
				isDismissible
					? setSuggestionGenerationState( { status: 'idle' } )
					: void null
			}
		>
			{ state.status === 'opportunity-scan-ready' ? (
				<SuggestionsBody
					opportunity={ state.opportunity }
					suggestions={ state.suggestions }
					instantiatingSuggestion={ state.instantiatingSuggestion }
				/>
			) : (
				<ScanningBody />
			) }
		</Modal>
	);
}

// ============
// HELPER VIEWS
// ============

const MESSAGE_DURATION_IN_MS = 2500;
const SCANNING_MESSAGES = [
	_x( 'Analyzing this opportunity…', 'text', 'nelio-ab-testing' ),
	_x( 'Exploring possibilities…', 'text', 'nelio-ab-testing' ),
	_x( 'Crafting test ideas…', 'text', 'nelio-ab-testing' ),
	_x( 'Almost there…', 'text', 'nelio-ab-testing' ),
] as const;

function ScanningBody(): JSX.Element {
	const [ messageIndex, setMessageIndex ] = useState( 0 );

	useEffect( () => {
		setTimeout( () => {
			setMessageIndex( messageIndex + 1 );
		}, MESSAGE_DURATION_IN_MS );
	}, [ messageIndex ] );

	return (
		<div className="nab-overview-suggestions-modal__loading-body">
			<LoadingAnimation />
			<div className="nab-overview-suggestions-modal__loading-text">
				{ SCANNING_MESSAGES[ messageIndex ] ??
					last( SCANNING_MESSAGES ) }
			</div>
		</div>
	);
}

function SuggestionsBody( {
	opportunity,
	suggestions,
	instantiatingSuggestion,
}: {
	readonly opportunity: Opportunity;
	readonly suggestions: ReadonlyArray< ExperimentSuggestion >;
	readonly instantiatingSuggestion: Maybe< ExperimentSuggestion >;
} ) {
	const opportunityBeingInstantiated = useSelect(
		( select ) => select( NAB_DATA ).getOpportunityBeingInstantiated(),
		[]
	);
	const { instantiateSuggestionAsExperiment } = useDispatch( NAB_DATA );
	return (
		<div className="nab-overview-suggestions-modal__suggestions-body">
			{ ! suggestions.length && (
				<div>
					{ sprintf(
						/* translators: %s: Nelio AI. */
						_x(
							'%s couldn’t come up with a suitable suggestion for this page.',
							'text',
							'nelio-ab-testing'
						),
						'Nelio AI'
					) +
						' ' +
						_x(
							'Start with a blank test and choose what you’d like to optimize.',
							'user',
							'nelio-ab-testing'
						) }
				</div>
			) }

			{ suggestions.map( ( suggestion, index ) => (
				<ExperimentSuggestionView
					key={ index }
					value={ suggestion }
					isBusy={ suggestion === instantiatingSuggestion }
					isDisabled={
						( !! instantiatingSuggestion &&
							instantiatingSuggestion !== suggestion ) ||
						!! opportunityBeingInstantiated
					}
					onCreateExperiment={ () =>
						instantiateSuggestionAsExperiment( suggestion )
					}
				/>
			) ) }

			<div className="nab-overview-suggestions-modal__fallback-experiment-action">
				<CreateExperimentFromOpportunityButton
					opportunity={ opportunity }
					isBusy={ opportunity === opportunityBeingInstantiated }
					isDisabled={ !! instantiatingSuggestion }
					labels={ {
						action: _x(
							'Create test from scratch',
							'command',
							'nelio-ab-testing'
						),
						busy: _x(
							'Creating test…',
							'text',
							'nelio-ab-testing'
						),
					} }
					variant={ suggestions.length ? 'link' : 'primary' }
				/>
			</div>
		</div>
	);
}

function ExperimentSuggestionView( {
	value,
	isBusy,
	isDisabled,
	onCreateExperiment,
}: {
	readonly value: ExperimentSuggestion;
	readonly isBusy: boolean;
	readonly isDisabled: boolean;
	readonly onCreateExperiment: () => void;
} ): JSX.Element {
	return (
		<div className="nab-overview-suggestions-modal__experiment-suggestion">
			<div className="nab-overview-suggestions-modal__experiment-suggestion-title">
				{ value.name }
			</div>
			{ !! value.description && (
				<div className="nab-overview-suggestions-modal__experiment-suggestion-description">
					{ value.description }
				</div>
			) }
			<div className="nab-overview-suggestions-modal__experiment-suggestion-actions">
				<Button
					variant="primary"
					isBusy={ isBusy }
					disabled={ isDisabled }
					onClick={ onCreateExperiment }
				>
					{ isBusy
						? _x( 'Creating Test…', 'command', 'nelio-ab-testing' )
						: _x( 'Create Test', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
}
